import React from 'react';

const CHUNK_ERROR_PATTERNS = [
  'Failed to fetch dynamically imported module',
  'Importing a module script failed',
  'error loading dynamically imported module',
];

function isChunkError(error: unknown): boolean {
  if (error instanceof Error) {
    return CHUNK_ERROR_PATTERNS.some((p) => error.message.includes(p));
  }
  return false;
}

interface State {
  hasChunkError: boolean;
}

export default class ChunkErrorBoundary extends React.Component<
  { children: React.ReactNode },
  State
> {
  state: State = { hasChunkError: false };

  static getDerivedStateFromError(error: unknown): State | null {
    if (isChunkError(error)) {
      return { hasChunkError: true };
    }
    return null;
  }

  componentDidCatch(error: unknown) {
    if (!isChunkError(error)) {
      throw error;
    }
  }

  render() {
    if (this.state.hasChunkError) {
      return (
        <div className="min-h-[100dvh] flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
          <div className="max-w-md w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-8 text-center shadow-xl">
            <div className="flex items-center justify-center w-14 h-14 mx-auto bg-blue-100 dark:bg-blue-900/40 rounded-full mb-5">
              <svg className="w-7 h-7 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              App Updated
            </h2>
            <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">
              We've just deployed a new version. Please refresh to load the
              latest experience.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-4 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
